import type { AnalyseArtikel, AnalyseCount, AnalysePalette, InventurAnalyse, InventurFreigabeExpected, InventurImportPreview, InventurPlace } from '@/api/inventur'
import { triggerBlobDownload } from './download'
export const statusText: Record<string, string> = {
  offen: 'Offen', bestaetigt: 'Bestätigt', abweichung: 'Abweichung', nachzaehlung_angefordert: 'Nachzählung angefordert',
  nachgezaehlt: 'Nachgezählt', nicht_gefunden: 'Nicht gefunden', soll_unbekannt: 'Soll unbekannt', nicht_zaehlbar: 'Nicht mehr zählbar',
  gebucht: 'Gebucht', unveraendert: 'Unverändert', verworfen: 'Verworfen', klaerfall: 'Klärfall', veraltet: 'Veraltet', kein_bestand: 'Kein Bestand',
}
export const countTypeText: Record<string, string> = { gezaehlt: 'Gezählt', pauschal_bestaetigt: 'Sichtbestätigung', leer: 'Platz leer',
  handregal: 'Handregal', zweitzaehlung_angeordnet: 'Nachzählung angefordert', nicht_gefunden: 'Nicht gefunden' }
export const defaultFilters = { suche: '', status: '', abweichung: '', minimum: '', mitarbeiter: '', von: '', bis: '', reihe: '' }
export type AnalyseFilters = typeof defaultFilters
export const menge = (n: number | null) => n === null ? '—' : n.toLocaleString('de-DE', { maximumFractionDigits: 3 })
export const differenzText = (n: number | null) => n === null ? '—' : `${n > 0 ? '+' : ''}${menge(n)}`
export const countMenge = (c: AnalyseCount | null) => !c ? null : ['nicht_gefunden', 'leer'].includes(c.countType) ? 0
  : c.countType === 'pauschal_bestaetigt' ? c.expectedQty : c.countedQty
type Einheit = AnalysePalette['einheit'] | null
export const einheitText = (unit: Einheit) => unit === 'm' ? 'm' : unit === 'stueck' ? 'St.' : 'Einheit unbekannt'
export const mengeMitEinheit = (value: number | null, unit: Einheit) => `${menge(value)} ${einheitText(unit)}`
/** Handregal quantities are pieces (handregal-logic.ts); palettes carry their own unit. */
export function artikelMengen(a: AnalyseArtikel) {
  const parts = [...a.paletten.map(p => ({ einheit: p.einheit, soll: p.soll, ist: p.ist })),
    ...(a.handregal ? [{ einheit: 'stueck' as const, soll: a.handregal.soll, ist: a.handregal.ist }] : [])]
  const units = [...new Set(parts.map(p => p.einheit))].sort((a, b) => a === b ? 0 : a === 'stueck' ? -1 : 1)
  // Keep the server totals for homogeneous articles, including historical sessions.
  if (units.length <= 1) return [{ einheit: units[0] ?? null, soll: a.soll, ist: a.ist, differenz: a.differenz }]
  const sum = (values: (number | null)[]) => values.some(v => v === null) ? null : values.reduce<number>((s, v) => s + (v ?? 0), 0)
  return units.map(einheit => {
    const group = parts.filter(p => p.einheit === einheit), soll = sum(group.map(p => p.soll)), ist = sum(group.map(p => p.ist))
    return { einheit, soll, ist, differenz: soll === null || ist === null ? null : ist - soll }
  })
}
export function artikelStatus(a: AnalyseArtikel) {
  if (!['bestaetigt', 'abweichung', 'soll_unbekannt'].includes(a.status)) return a.status
  const values = artikelMengen(a)
  return values.some(v => v.differenz === null) ? 'soll_unbekannt' : values.some(v => v.differenz !== 0) ? 'abweichung' : 'bestaetigt'
}
export function abweichungsSummen(rows: AnalyseArtikel[], sign: 'plus' | 'minus') {
  const sums = new Map<Einheit, number>()
  for (const a of rows) for (const v of artikelMengen(a)) {
    const n = v.differenz
    if (n !== null && (sign === 'plus' ? n > 0 : n < 0)) sums.set(v.einheit, (sums.get(v.einheit) ?? 0) + n)
  }
  return [...sums].sort(([a], [b]) => a === b ? 0 : a === 'stueck' ? -1 : 1)
    .map(([unit, n]) => `${differenzText(n)} ${einheitText(unit)}`).join(' · ') || '0'
}
export function countEinheit(c: AnalyseCount, palettes: AnalysePalette[]): Einheit {
  return c.countType === 'handregal' ? 'stueck' : palettes.find(p => p.positionId === c.positionId)?.einheit ?? null
}
export function decisionEinheit(d: { positionId: number | null; ziel: string }, rows: AnalyseArtikel[]): Einheit {
  return d.ziel === 'handregal' ? 'stueck' : rows.flatMap(a => a.paletten).find(p => p.positionId === d.positionId)?.einheit ?? null
}
export function countMatches(c: AnalyseCount, f: AnalyseFilters) {
  return (!f.mitarbeiter || c.countedByName === f.mitarbeiter)
    && (!f.von || new Date(c.createdAt).getTime() >= new Date(`${f.von}T00:00:00`).getTime())
    && (!f.bis || new Date(c.createdAt).getTime() <= new Date(`${f.bis}T23:59:59.999`).getTime())
}
export function filterArtikel(rows: AnalyseArtikel[], f: AnalyseFilters, protocol: AnalyseCount[] = []): AnalyseArtikel[] {
  const q = f.suche.trim().toLocaleLowerCase('de-DE')
  return rows.filter(a => {
    const counts = [...a.paletten.flatMap(p => [p.firstCount, p.secondCount, p.effectiveCount]), a.handregal?.count,
      ...protocol.filter(c => c.merchantSku === a.merchantSku || a.paletten.some(p => p.positionId === c.positionId)
        || (c.stockItemId !== null && c.stockItemId === a.handregal?.count?.stockItemId))].filter((c): c is AnalyseCount => !!c)
    return (!q || [a.merchantSku, a.itemName, ...a.paletten.flatMap(p => [p.placeBarcode, p.paletteNo])].some(v => v?.toLocaleLowerCase('de-DE').includes(q)))
      && (!f.status || artikelStatus(a) === f.status || a.paletten.some(p => p.status === f.status))
      && (!(f.abweichung || f.minimum) || (f.abweichung === 'keine'
        ? artikelMengen(a).every(v => v.differenz === 0) && !Number(f.minimum)
        : artikelMengen(a).some(v => v.differenz !== null
          && (!f.abweichung || (f.abweichung === 'plus' ? v.differenz > 0 : v.differenz < 0))
          && Math.abs(v.differenz) >= Math.max(0, Number(f.minimum)))))
      && (!(f.mitarbeiter || f.von || f.bis) || counts.some(c => countMatches(c, f)))
      && (!f.reihe || a.paletten.some(p => p.rowCode === f.reihe))
  })
}
function csvText(value: string): string {
  const safe = /^[=+\-@\t\r\n]/.test(value) ? `'${value}` : value
  return /[;"\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}
export function analyseCsv(rows: AnalyseArtikel[]) {
  return '\uFEFF' + ['Artikel-Nr.;Artikel;Einheit;Stahlia-Soll;Ist;Differenz;Status', ...rows.flatMap(a => artikelMengen(a).map(v => [csvText(a.merchantSku), csvText(a.itemName ?? ''),
    einheitText(v.einheit), menge(v.soll), menge(v.ist), differenzText(v.differenz), statusText[artikelStatus(a)]].join(';')))].join('\r\n')
}
export function downloadAnalyseCsv(rows: AnalyseArtikel[], id: number) {
  downloadCsv(analyseCsv(rows), `inventur-${id}-gefiltert.csv`)
}
export function downloadCsv(csv: string, filename: string) {
  triggerBlobDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename)
}
export function protocolCsv(counts: InventurAnalyse['zaehlprotokoll'], palettes: AnalysePalette[]) {
  return '\uFEFF' + ['Zeitpunkt;Person;Gerät;Artikel;Platz;Palette;Menge;Einheit;Typ;Count-ID', ...counts.map(c => [
    csvText(new Date(c.createdAt).toLocaleString('de-DE')), csvText(c.countedByName ?? ''), csvText(c.deviceLabel ?? ''),
    csvText(c.merchantSku ?? ''), csvText(c.placeBarcode ?? ''), csvText(palettes.find(p => p.positionId === c.positionId)?.paletteNo ?? ''),
    c.countType === 'zweitzaehlung_angeordnet' ? '—' : menge(countMenge(c)), einheitText(countEinheit(c, palettes)), csvText(countTypeText[c.countType] ?? c.countType), c.id,
  ].join(';'))].join('\r\n')
}
export function placesCsv(places: InventurPlace[]) {
  return '\uFEFF' + ['Platz;Reihe;Zone;Ebene;Einheit', ...places.map(p => [csvText(p.barcode), csvText(p.rowCode), p.zone === 'stapler' ? 'Stapler' : 'Zu Fuß', p.levelNo, 'nicht zutreffend'].join(';'))].join('\r\n')
}
export function importScope(report: InventurImportPreview) {
  const allowed = report.rows.filter(r => ['ok', 'kein_bestand'].includes(r.status) && r.merchant_sku).map(r => r.merchant_sku!)
  return { merchant_skus: [...new Set(allowed)], import: {
    rows: report.rows.map(({ input, status, merchant_sku, duplicate }) => ({ input, status, merchant_sku, duplicate })),
  } }
}
export function expectedForArticle(article: AnalyseArtikel): InventurFreigabeExpected {
  return { palette: article.paletten.map(p => ({ position_id: p.positionId, count_id: p.effectiveCount?.id ?? null })),
    ...(article.handregal ? { handregal_count_id: article.handregal.count?.id ?? null } : {}) }
}
export function palettePlan(p: AnalysePalette) {
  if (p.status === 'nicht_gefunden') return 'Klärfall: Palette fehlt, keine Buchung'
  if (p.status === 'nicht_zaehlbar') return 'Klärfall: nicht mehr zählbar, keine Buchung'
  if (p.offen || p.pending) return 'Gesperrt: Palette noch offen oder Nachzählung angefordert'
  return `${mengeMitEinheit(p.soll, p.einheit)} → ${mengeMitEinheit(p.ist, p.einheit)} (${p.differenz === 0 ? 'Unverändert' : 'Buchung nach Änderungsschutz-Prüfung'})`
}
